import {gql} from '@apollo/client';
import {Stack, Grid, Text, Title, Tooltip, Box, createStyles} from '@mantine/core';
import {DocumentNode} from 'graphql';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {USER_SUMMARY_FIELDS_FRAGMENT} from 'parties/users/UserSummaryFragment';
import TenantRoleListWithPopover from 'tenants/admin/TenantRoleListWithPopover';
import useOnMount from 'ui-library/hooks/useOnMount';
import * as DateUtil from 'util/DateUtil';

import {UserPageQuery} from './__generated/UserPageQuery';
import {UserAPIKeySection, WarehouseCredentialsTable, UserMFASection} from './UserCredentialsSections';
import type {RefetchUserPage} from './UserPage';

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;

export const USER_BASIC_SETTINGS_TAB_USER_FRAGMENT: DocumentNode = gql`
  fragment UserBasicSettingsTabUser on UserNode {
    ...UserSummaryFields
    mfaEnabled
    activeWarehouseLogins {
      id
      username
      label
      createdAt
    }
    organizations {
      id
      name
      handle
      urlIdentifier
    }
    teams {
      id
      name
      handle
    }
    apiKey {
      createdAt
    }
    permissions {
      canResetMfa
    }
  }
  ${USER_SUMMARY_FIELDS_FRAGMENT}
`;

export interface UserBasicSettingsTabProps {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
}

export function UserAttribute({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  return (
    <Stack spacing="sm" pt="xs" pl="xs" align="flex-start">
      <Text fw={700}>{label}</Text>
      {value}
    </Stack>
  );
}

const MAX_VISIBLE_ITEMS = 2;

function TruncatedList({names, max = MAX_VISIBLE_ITEMS}: {names: Array<string>; max?: number}): JSX.Element {
  const shown = names.slice(0, max);
  const extraCount = names.length - shown.length;
  const label = extraCount > 0 ? `${shown.join(', ')} +${extraCount} more` : shown.join(', ');

  if (names.length === 0) {
    return <Text>None</Text>;
  }

  return extraCount > 0 ? (
    <Tooltip label={names.join(', ')} multiline={true} withArrow={true}>
      <Text>{label}</Text>
    </Tooltip>
  ) : (
    <Text>{label}</Text>
  );
}
interface UserBasicSettingsAttributes {
  [key: string]: {
    value: React.ReactNode;
    span?: number;
    visible?: boolean;
  };
}

export default function UserBasicSettingsTab({user, refetch}: UserBasicSettingsTabProps): JSX.Element {
  const {partiesEnableNewUserProfileTacPage} = useRolloutFlags();

  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_PARTY_BASIC_SETTINGS_TAB, {
      objectType: 'user',
      objectId: user.id,
    });
  });

  const accountAttributes: UserBasicSettingsAttributes = {
    // TODO(BNCH-166833): Wait for Mantine EditableTextField
    Name: {
      value: user.name,
    },
    Username: {value: user.handle},
    Email: {
      value: user.email,
    },
    'Joined On': {
      value: <Text>{DateUtil.readableTime(user.joinedOn)}</Text>,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Organizations: {
      value: <TruncatedList names={user.organizations.map((org) => org.name)} />,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Teams: {
      value: <TruncatedList names={user.teams.map((team) => team.name)} />,
      visible: partiesEnableNewUserProfileTacPage,
    },
    Roles: {
      value: user.tenantCollaborations.length ? (
        <TenantRoleListWithPopover party={user} />
      ) : (
        <Text>None</Text>
      ),
      visible: partiesEnableNewUserProfileTacPage,
    },
  };

  const credentialsAttributes: UserBasicSettingsAttributes = {
    Password: {
      value: (
        <Text>
          {user.passwordLastChangedAt
            ? `Set on ${DateUtil.readableTime(user.passwordLastChangedAt)}`
            : 'Never changed'}
        </Text>
      ),
      visible: partiesEnableNewUserProfileTacPage,
    },
    'MFA Status': {
      value: <UserMFASection user={user} refetch={refetch} />,
      visible: BenchlingConfig.AUTH_ENABLE_MFA_FEATURE,
    },
    'API Keys': {
      value: <UserAPIKeySection user={user} refetch={refetch} />,
      span: 2,
      visible: partiesEnableNewUserProfileTacPage && !(user.apiKey == null),
    },
    'Warehouse Credentials': {
      value: <WarehouseCredentialsTable user={user} refetch={refetch} />,
      span: 2,
      visible: partiesEnableNewUserProfileTacPage && user.activeWarehouseLogins.length > 0,
    },
  };

  const sections: Array<{title: string; attributes: UserBasicSettingsAttributes; visible?: boolean}> = [
    {title: 'User Account', attributes: accountAttributes},
    {
      title: 'Credentials',
      attributes: credentialsAttributes,
      // This should get removed when both the LD flag and MFA feature goes GA
      visible: partiesEnableNewUserProfileTacPage || BenchlingConfig.AUTH_ENABLE_MFA_FEATURE,
    },
  ];

  const {classes} = styles();
  return (
    <Box className={classes.basicSettingsTab} pt="xl">
      <Stack spacing="xl">
        {sections.map(
          ({title, attributes, visible = true}) =>
            visible && (
              <Stack key={title} spacing="xs">
                <Title order={4}>{title}</Title>
                <Grid columns={2}>
                  {Object.entries(attributes).map(
                    ([label, {value, span = 1, visible = true}]) =>
                      visible && (
                        <Grid.Col key={label} span={span}>
                          <UserAttribute label={label} value={value} />
                        </Grid.Col>
                      )
                  )}
                </Grid>
              </Stack>
            )
        )}
      </Stack>
    </Box>
  );
}

const styles = createStyles((theme) => ({
  basicSettingsTab: {
    paddingInline: 'calc(100% / 22)',
    maxWidth: theme.breakpoints.lg,
  },
}));
